import React, { useCallback, useState, useEffect } from 'react'
import { Box } from 'rebass'
import { ThemeProvider } from 'emotion-theming'
import { Global } from '@emotion/core'

import theme, { THEMES } from '../theme'
import Home from '../app/Home'

import DigitalHuman from '../app/DigitalHuman'
import SessionTimedOut from '../app/SessionTimedOut'
import FatalError from '../customPlugins/MayaErrors/FatalError'
import { useApplicationState } from '../hooks/useApplicationState'
import { useAutoResize } from '../hooks/useAutoResize'
import { useScrollToTop } from '../hooks/useScrollToTop'
import { Login } from './Login'
import TvContext from '../app/contexts/TvContext'
import { MainMenu } from './MainMenu'
import { useSpatialNavigation } from './hooks/useSpatialNavigation'
import { IntroductionVideo } from './MainMenu/IntroductionVideo/IntroductionVideo'
import { StorageKeys } from './types/StorageKeys'
import { Disclaimer } from './MainMenu/Disclaimer/Disclaimer'
import { endSession } from '../socket'
import { Todo } from './types/Todo'

const backgroundStyle = {
  label: 'wrapper',
  width: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'right bottom',
  backgroundColor: '#004C9C',
  overflow: 'hidden',
  position: 'absolute',
  top: 0,
  left: 0
}

type TvSteps =
  | 'welcome'
  | 'digitalHuman'
  | 'timed-out'
  | 'login'
  | 'mainMenu'
  | 'introductionVideo'
  | 'disclaimer'

const clearStorage = () => {
  localStorage.removeItem(StorageKeys.API_USER)
  localStorage.removeItem(StorageKeys.API_TOKEN)
}

const App = () => {
  const queryParams = new URLSearchParams(window.location.search)
  const timeoutTestTime = Number(queryParams.get('test_timeout_time'))

  const [step, goTo] = useState<TvSteps>('login')
  const [speak, setSpeak] = useState(true)
  useSpatialNavigation()

  useEffect(() => {
    // @ts-ignore
    const SpatialNavigation = window.SpatialNavigation
    if (SpatialNavigation) {
      SpatialNavigation.makeFocusable()
    }
  })

  const {
    currentLanguage,
    disableDH,
    personaId,
    headingMessage,
    hideDownloadNotes,
    initError,
    logo,
    supportedLanguages,
    session,
    disclaimer,
    uneeqURL
  } = useApplicationState({})

  const restart = () => goTo('mainMenu')

  const stepIsDigitalHuman = useCallback(() => step === 'digitalHuman', [step])

  const height = useAutoResize({ condition: stepIsDigitalHuman })
  useScrollToTop({ condition: stepIsDigitalHuman })

  const navigateToDigitalHuman = useCallback(() => goTo('welcome'), [])
  const navigateToVideo = useCallback(() => goTo('introductionVideo'), [])
  const navigateToMainMenu = useCallback(() => goTo('mainMenu'), [])
  const navigateToDisclaimer = useCallback(() => goTo('disclaimer'), [])

  const handleNavigateWellnessCheck = useCallback(() => {
    // We need to push to the history so when the user
    // tries to go back with control remote, the popstate event
    // will be triggered
    window.history.pushState('wellness', '')
    navigateToDigitalHuman()
  }, [navigateToDigitalHuman])

  const acceptDisclaimer = () => {
    localStorage.setItem(StorageKeys.DISCLAIMER_SHOWN, 'true')
    navigateToMainMenu()
  }

  useEffect(() => {
    const onNavigateBackFromWellness = () => {
      if (step === 'welcome' || step === 'digitalHuman') {
        navigateToMainMenu()
      }
    }

    window.addEventListener('popstate', onNavigateBackFromWellness)

    return () =>
      window.removeEventListener('popstate', onNavigateBackFromWellness)
  }, [navigateToMainMenu, step])

  const logout = useCallback(() => {
    clearStorage()
    goTo('login')
  }, [])

  const [wellnessCheckInTodoId, setWellnessCheckInTodoId] = useState('')
  const [symptomIds, setSymptomIds] = useState([])
  const [todoList, setTodoList] = useState<Todo[] | null>(null)

  return (
    <ThemeProvider theme={theme(THEMES.DARK, false)}>
      <Global styles={theme => ({ body: { fontFamily: theme.fonts.body } })} />
      <TvContext.Provider
        value={{
          tvAppMode: true,
          wellnessCheckInTodoId,
          setWellnessCheckInTodoId,
          symptomIds,
          todoList,
          setTodoList,
          setSymptomIds,
          session: session || ''
        }}
      >
        <Box
          sx={{
            ...backgroundStyle,
            height: height || '100%',
            overflow: step === 'welcome' ? 'visible' : 'hidden'
          }}
        >
          {initError && (
            <FatalError
              errorTitle="Something went wrong"
              errorMessage={initError}
              clearError={window.location.reload}
              buttonText="Try again"
            />
          )}
          {step === 'login' ? (
            <Login onSuccess={navigateToVideo} />
          ) : step === 'digitalHuman' ? (
            <DigitalHuman
              speak={speak}
              onTimedOut={() => goTo('timed-out')}
              restart={restart}
              embeddedMode={false}
              onSessionEnded={() => {
                restart()
                endSession()
              }}
              personaId={personaId}
              uneeqURL={uneeqURL}
              testTimeoutTime={timeoutTestTime}
              hideDownloadNotes={hideDownloadNotes}
            />
          ) : step === 'timed-out' ? (
            <SessionTimedOut restart={restart} />
          ) : step === 'mainMenu' ? (
            <MainMenu
              onWellnessCheck={handleNavigateWellnessCheck}
              setWellnessCheckInTodoId={setWellnessCheckInTodoId}
              onLogout={logout}
            />
          ) : step === 'introductionVideo' ? (
            <IntroductionVideo onFinishVideo={navigateToDisclaimer} />
          ) : step === 'disclaimer' ? (
            <Disclaimer
              handleAccept={acceptDisclaimer}
              handleDecline={logout}
              disclaimerInfo={disclaimer}
            />
          ) : (
            <Home
              showVideoBackground
              embeddedMode={false}
              startSession={speak => {
                setSpeak(speak)
                goTo('digitalHuman')
              }}
              restart={() => goTo('welcome')}
              headingMessage={headingMessage}
              disableDigitalHuman={disableDH}
              logo={logo}
              languageSelected={currentLanguage}
              supportedLanguages={supportedLanguages}
            />
          )}
        </Box>
      </TvContext.Provider>
    </ThemeProvider>
  )
}

export default App
